<template>
    <div
        class="dropdown-cell"
        :class="{
            [field.alignment]: true,
            outlined,
        }"
        @click="enableEdit"
    >
        <template v-if="editing">
            <a-popover trigger="click" placement="bottomLeft" v-model="editing" @visibleChange="onVisibleChange">
                <template #content>
                    <div class="dropdown-options">
                        <template v-if="isDynamicMode">
                            <a-input-search
                                placeholder="Search"
                                v-model="searchText"
                                @change="handleSearchChange"
                                :loading="isSearching"
                                autoFocus
                            />
                        </template>
                        <template v-else>
                            <a-input-search placeholder="Search" v-model="searchText" autoFocus />
                        </template>

                        <a-button 
                            v-if="selectedOptions.length > 0"
                            type="link" 
                            class="clear-button" 
                            @click="clearSelection"
                        >
                            <a-icon type="delete" /> Clear Selection
                        </a-button>

                        <div
                            v-for="option in displayOptions"
                            :key="option.value"
                            class="option-item"
                            @click="onOptionClick(option.value)"
                        >
                            <a-checkbox v-if="isMultiple" :checked="selectedOptions.includes(option.value)" />
                            <div class="option-chip" :style="{ backgroundColor: option.color || 'rgb(210, 216, 243)' }">
                                {{ option.value }}
                            </div>
                        </div>
                    </div>
                </template>
                <div class="selected-options" ref="selectedOptionsRef">
                    <div
                        v-for="option in selectedOptions"
                        :key="option"
                        class="option-chip"
                        :style="{ backgroundColor: getOptionColor(option) }"
                    >
                        {{ option }}
                    </div>
                </div>
            </a-popover>
        </template>
        <template v-else>
            <div class="selected-options" ref="selectedOptionsRef">
                <div
                    v-for="option in selectedOptions"
                    :key="option"
                    class="option-chip"
                    :style="{ backgroundColor: getOptionColor(option) }"
                >
                    {{ option }}
                </div>
            </div>
            <div class="chevron-icons">
                <a-icon type="up" />
                <a-icon type="down" />
            </div>
        </template>
    </div>
</template>

<script>
import Vue from "vue";
import _ from "lodash";
import { TABLE_API } from "../../../../../api/table";
import log2 from "../../../../../utils/log2";

const log = log2("DropdownCell");
export default Vue.extend({
    name: "DropdownCell",
    props: {
        record: {
            type: Object,
            required: true,
        },
        field: {
            type: Object,
            required: true,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        outlined: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            editing: false,
            searchText: "",
            selectedOptions: [],
            isSearching: false,
            dynamicOptions: [],
        };
    },
    computed: {
        isMultiple() {
            return this.field.options.dropdown.multiple;
        },
        isDynamicMode() {
            return this.field.options.dropdown.mode === "dynamic";
        },
        dropdownOptions() {
            return this.field.options.dropdown.options || [];
        },
        displayOptions() {
            if (this.isDynamicMode) {
                return this.dynamicOptions;
            }

            return this.dropdownOptions.filter((option) =>
                option.value.toLowerCase().includes(this.searchText.toLowerCase())
            );
        },
    },
    methods: {
        async enableEdit() {
            if (this.readonly) {
                this.$message.error("Seçim modu içinde bu alanı düzenleyemezsiniz");
                return;
            }
            this.editing = true;
            this.selectedOptions = this.getSelectedOptions();
            log(`enableEdit isDynamicMode=${this.isDynamicMode}`);

            if (this.isDynamicMode) {
                this.searchText = "";
                await this.handleSearchChange();
            }
        },
        async disableEdit() {
            this.editing = false;
            try {
                const newValue = this.isMultiple ? this.selectedOptions : this.selectedOptions[0] || null;
                const isChanged = !this.isEqual(this.record.data[this.field._id], newValue);
                console.log("disableEdit", {
                    isChanged,
                    oldValue: this.record.data[this.field._id],
                    newValue: newValue,
                });
                if (isChanged) {
                    await this.$emit("update-record", {
                        recordId: this.record._id,
                        fieldId: this.field._id,
                        value: newValue,
                    });
                }
            } catch (error) {
                console.error(this.$t("tables.recordUpdatingError"), error);
                this.$message.error(this.$t("tables.recordUpdatingError"));
            }
        },
        generateColorFromText(text) {
            // Metni hash'e çevir
            let hash = 0;
            for (let i = 0; i < text.length; i++) {
                hash = text.charCodeAt(i) + ((hash << 5) - hash);
            }

            // Siyah yazı için daha açık ve soft pastel renkler kullan
            const hue = Math.abs(hash) % 360; // 0-360 arası
            const saturation = 25 + (Math.abs(hash) % 15); // 25-40 arası - daha soft
            const lightness = 85 + (Math.abs(hash) % 10); // 85-95 arası - daha açık

            return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
        },
        handleSearchChange: _.debounce(async function () {
            log("handleSearchChange", {
                isDynamicMode: this.isDynamicMode,
                searchText: this.searchText,
            });
            if (!this.isDynamicMode) {
                this.dynamicOptions = [];
                return;
            }

            this.isSearching = true;
            try {
                const remoteTableId = this.field.options.dropdown.remoteTable;
                const remoteTableField = this.field.options.dropdown.remoteTableField;
                const results = await TABLE_API.searchFieldValues(
                    remoteTableId,
                    remoteTableField,
                    this.searchText || ""
                );

                this.dynamicOptions = results.map((item) => ({
                    value: item,
                    color: this.generateColorFromText(item),
                }));
            } catch (error) {
                console.error("Arama hatası:", error);
                this.$message.error(this.$t("tables.searchError"));
            } finally {
                this.isSearching = false;
            }
        }, 300),
        isEqual(a, b) {
            if (this.isMultiple) {
                return (
                    Array.isArray(a) &&
                    Array.isArray(b) &&
                    a.length === b.length &&
                    a.every((val, index) => val === b[index])
                );
            } else {
                return a === b;
            }
        },
        updateRecord() {
            const updatedRecord = {
                ...this.record,
                [this.field._id]: this.isMultiple ? this.selectedOptions : this.selectedOptions[0],
            };
            this.$emit("update-record", updatedRecord);
        },
        getOptionColor(option) {
            if (this.isDynamicMode) {
                return this.generateColorFromText(option);
            } else {
                const optionObj = this.dropdownOptions.find((opt) => opt.value === option);
                return optionObj ? optionObj.color : "";
            }
        },
        getSelectedOptions() {
            const value = this.record.data[this.field._id];
            if (this.isMultiple) {
                return Array.isArray(value) ? value : [];
            } else {
                return value ? [value] : "";
            }
        },
        onOptionChange(option) {
            if (this.selectedOptions.includes(option)) {
                this.selectedOptions = this.selectedOptions.filter((opt) => opt !== option);
            } else {
                this.selectedOptions.push(option);
            }
        },
        onOptionClick(option) {
            if (this.isMultiple) {
                if (this.selectedOptions.includes(option)) {
                    this.selectedOptions = this.selectedOptions.filter((opt) => opt !== option);
                } else {
                    this.selectedOptions.push(option);
                }
            } else {
                if (this.selectedOptions[0] === option) {
                    this.selectedOptions = [];
                } else {
                    this.selectedOptions = [option];
                }
                this.disableEdit();
            }
        },
        async onVisibleChange(visible) {
            if (!visible) {
                await this.disableEdit();
            }
        },
        clearSelection() {
            this.selectedOptions = [];
            if (!this.isMultiple) {
                this.disableEdit();
            }
        },
    },
    mounted() {
        this.selectedOptions = this.getSelectedOptions();
    },
});
</script>

<style scoped lang="scss">
.dropdown-cell {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    padding: 4px 8px;
    cursor: pointer;
    transition: all 0.3s;

    &.outlined {
        border: 1px solid #d9d9d9;
        border-radius: 4px;

        &:hover {
            border-color: #40a9ff;
        }

        &:active {
            border-color: #096dd9;
        }
    }

    .selected-options {
        display: flex;
        overflow-x: auto;
        white-space: nowrap;
        -ms-overflow-style: none;
        scrollbar-width: none;

        &::-webkit-scrollbar {
            display: none;
        }
    }

    .chevron-icons {
        position: absolute;
        top: 50%;
        right: 5px;
        transform: translateY(-50%);
        display: flex;
        flex-direction: column;
    }
}

.dropdown-options {
    max-height: 200px;
    overflow-y: auto;

    .ant-input-search {
        margin-bottom: 8px;
    }

    .clear-button {
        width: 100%;
        text-align: left;
        margin-bottom: 8px;
        color: #ff4d4f;
        
        &:hover {
            background-color: #fff1f0;
        }
    }
}

.option-item {
    display: flex;
    align-items: center;
    padding: 4px 8px;
    cursor: pointer;

    &:hover {
        background-color: #f5f5f5;
    }

    .option-chip {
        margin-left: 8px;
    }
}

.option-chip {
    display: inline-block;
    padding: 2px 6px;
    border-radius: 16px;
    margin-right: 4px;
    cursor: pointer;
}
</style>
